import i18n from 'i18next';
// Localises known notification types on the client so in-app notifications can
// render in the viewer's language (sv/en) via i18next, instead of only showing
// the Swedish text generated and stored server-side. Unknown event types fall
// back to the stored title/body unchanged.

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface LocalizableNotification {
  eventKey?: string | null;
  title: string;
  body: string;
  metadata?: Record<string, unknown> | null;
}

export const localizeNotification = (
  n: LocalizableNotification,
  t: Translate,
): { title: string; body: string } => {
  if (['payment.funding_required', 'payment.funding_reminder', 'payment.unfunded_start', 'payment.funding_fulfilled'].includes(n.eventKey ?? '') && n.metadata?.fundingSnapshot && typeof n.metadata.fundingSnapshot === 'object') {
    const data = n.metadata.fundingSnapshot as Record<string, unknown>;
    if (Array.isArray(data.stages) && typeof data.requiredAmountOre === 'number' && typeof data.availableBalanceOre === 'number' && typeof data.amountOre === 'number') {
      const key = 'fundingNotice.projectAccount';
      const money = (ore: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK' }).format(ore / 100);
      const stageList = (data.stages as Array<{ contractNumber: number; milestoneNumber: number; amountOre: number }>).map(stage => t(`${key}.stageLabel`, { contract: stage.contractNumber, milestone: stage.milestoneNumber, amount: money(stage.amountOre) })).join('; ');
      const values = { projectDescription: n.metadata.projectDescription, contractNumber: data.contractNumber, stageList, balance: money(data.availableBalanceOre), required: money(data.requiredAmountOre), amount: money(data.amountOre) };
      const fulfilled = n.eventKey === 'payment.funding_fulfilled';
      const prefix = n.eventKey === 'payment.unfunded_start' ? 'start' : n.eventKey === 'payment.funding_reminder' ? 'reminder' : 'required';
      const bodyKey = fulfilled ? data.recipientRole === 'company' ? 'fulfilledCompanyBody' : 'fulfilledPersonBody' : prefix === 'start' ? 'startBody' : prefix === 'reminder' ? 'accountReminderBody' : 'accountRequiredBody';
      const started = data.startedMilestone as { contractNumber: number; milestoneNumber: number; amountOre: number } | undefined;
      const startedLine = started ? [t(`${key}.startedStage`, { stage: t(`${key}.stageLabel`, { contract: started.contractNumber, milestone: started.milestoneNumber, amount: money(started.amountOre) }) })] : [];
      return { title: t(`${key}.${fulfilled ? 'fulfilledTitle' : `${prefix}Title`}`, values), body: [t(`${key}.${bodyKey}`, values), ...startedLine, t(`${key}.contextBody`, values), t(`${key}.planning`), t(`${key}.${fulfilled ? 'remindersStopped' : 'optionalRemainder'}`)].join(' ') };
    }
  }
  if (['payment.funding_required', 'payment.funding_reminder'].includes(n.eventKey ?? '') && typeof n.metadata?.amountOre === 'number') {
    const prefix = n.eventKey === 'payment.funding_reminder' ? 'reminder' : 'required';
    return {
      title: t(`fundingNotice.${prefix}Title`),
      body: t(`fundingNotice.${prefix}Body`, {
        contractNumber: n.metadata.contractNumber,
        projectDescription: n.metadata.projectDescription,
        amount: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK' }).format(n.metadata.amountOre / 100)
      })
    };
  }
  if (['payout.reversed', 'payout.failed'].includes(n.eventKey ?? '') &&
      n.metadata?.adyenTransferStatus === 'refused' && n.metadata?.adyenTransferReason === 'notEnoughBalance') {
    return { title: t('payoutAvailability.title'), body: t('payoutAvailability.notificationBody') };
  }
  // A contract the customer sent back with an objection (ClickUp 869fcmr7k):
  // the objection is shown in full. The same event key is also used for a
  // declined signing, which keeps its stored text.
  if (n.eventKey === 'contract.draft_rejected' && n.metadata?.returnedByCustomer === true) {
    const comment = typeof n.metadata.comment === 'string' ? n.metadata.comment.trim() : '';
    // An ÄTA is named "ÄTA #n", not "kontrakt #n" (ClickUp 869fnvq6j).
    const number = typeof n.metadata.variationNumber === 'number' ? n.metadata.variationNumber : null;
    const suffix = number != null ? 'Variation' : '';
    return {
      title: t(`contractNotice.returnedTitle${suffix}`),
      body: comment
        ? t(`contractNotice.returnedBodyComment${suffix}`, { contractNumber: n.metadata.contractNumber, number, comment, interpolation: { escapeValue: false } })
        : t(`contractNotice.returnedBody${suffix}`, { contractNumber: n.metadata.contractNumber, number })
    };
  }
  // A signed ÄTA (ClickUp 869fnvq6j): named by its direction, with the next
  // step for the recipient. An ordinary contract keeps its stored text.
  const direction = n.metadata?.variationDirection;
  if (n.eventKey === 'contract.signed_by_both' && (direction === 'addition' || direction === 'removal' || direction === 'change')) {
    const number = typeof n.metadata?.variationNumber === 'number' ? n.metadata.variationNumber : null;
    const role = n.metadata?.variationRecipientRole === 'company' ? 'company' : 'person';
    const amountOre = typeof n.metadata?.variationAmountOre === 'number' ? n.metadata.variationAmountOre : 0;
    const amount = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK', maximumFractionDigits: amountOre % 100 === 0 ? 0 : 2 }).format(amountOre / 100);
    return {
      title: number != null
        ? t(`contractNotice.variationSigned.title.${direction}`, { number })
        : t(`contractNotice.variationSigned.titleNoNumber.${direction}`),
      body: direction === 'change'
        ? t('contractNotice.variationSigned.body.change')
        : t(`contractNotice.variationSigned.body.${direction}.${role}`, { amount })
    };
  }
  // "Be beställaren attestera" (ClickUp 869fnf0e5): the notification asks for
  // the attestation; a request about a change keeps its stored text.
  if (n.eventKey === 'milestone.message_posted' && n.metadata?.attestationRequest === true &&
      typeof n.metadata.milestoneNumber === 'number') {
    const preview = typeof n.metadata.messagePreview === 'string' ? n.metadata.messagePreview.trim() : '';
    return {
      title: t('milestoneNotice.attestationRequestTitle', { number: n.metadata.milestoneNumber }),
      body: preview
        ? t('milestoneNotice.attestationRequestBody', { preview, interpolation: { escapeValue: false } })
        : t('milestoneNotice.attestationRequestBodyEmpty', { number: n.metadata.milestoneNumber })
    };
  }
  if (n.eventKey === 'payout.booked') {
    const isFinal =
      n.metadata?.paymentKind === 'final' || n.metadata?.isFinalPayout === true;
    const label = isFinal
      ? t('projectView.transactions.notifications.payoutFinal')
      : t('projectView.transactions.notifications.payoutPartial');
    const payee =
      (typeof n.metadata?.payeeCompanyName === 'string' &&
        n.metadata.payeeCompanyName) ||
      t('projectView.transactions.notifications.payeeFallback');
    // What happened for the recipient: the amount paid out, never how the
    // money moves or that the fee is booked (ClickUp 869fnvqfy). An older
    // notice without a recipient role gets a neutral text.
    const amountOre = typeof n.metadata?.payoutAmountOre === 'number' ? n.metadata.payoutAmountOre : 0;
    const milestone = typeof n.metadata?.milestoneNumber === 'number' ? n.metadata.milestoneNumber : null;
    const role = n.metadata?.recipientRole === 'company' ? 'Company' : n.metadata?.recipientRole === 'person' ? 'Person' : 'Neutral';
    return {
      title: label,
      body: t(`projectView.transactions.notifications.payoutBody${role}${milestone == null ? 'NoStage' : ''}`, {
        amount: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK', maximumFractionDigits: amountOre % 100 === 0 ? 0 : 2 }).format(amountOre / 100),
        milestone,
        project: n.metadata?.projectNumber,
        payee,
      }),
    };
  }
  return { title: n.title, body: n.body };
};
